import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Building,
  Home,
  Edit,
  Settings,
  Mail,
  Phone,
  Lock,
  Key,
  EyeOff,
  Eye,
  Check,
  X,
  Shield,
  Clock,
  User,
  Briefcase,
} from "lucide-react";
import {
  checkActiveUser,
  resetPassword,
  updateUserInfo,
} from "../../store/slices/authSlice";
import { fetchSocieties } from "../../store/slices/societySlice";
import { Transition } from "@headlessui/react";
import Swal from "sweetalert2";
import { useSidebarContext } from "../../scenes/global/sidebar/SidebarContext";

const PasswordStrengthIndicator = ({ password }) => {
  const hasLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const { theme } = useSidebarContext();

  const calculateStrength = () => {
    let strength = 0;
    if (hasLength) strength++;
    if (hasUpperCase) strength++;
    if (hasLowerCase) strength++;
    if (hasNumber) strength++;
    if (hasSymbol) strength++;

    if (strength <= 2) return { text: "Weak", color: "bg-red-500", strength };
    if (strength <= 4)
      return { text: "Medium", color: "bg-yellow-500", strength };
    return { text: "Strong", color: "bg-green-500", strength };
  };

  const strength = calculateStrength();

  return (
    <div className="space-y-4 mt-4">
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className={`text-sm ${theme.tailwind.texting2}`}>
            Password Strength:
          </span>
          <span
            className={`text-sm font-medium ${
              strength.text === "Weak"
                ? "text-red-400"
                : strength.text === "Medium"
                ? "text-yellow-400"
                : "text-green-400"
            }`}
          >
            {strength.text}
          </span>
        </div>
        <div
          className={`h-2 w-full ${theme.tailwind.bg5} rounded-full overflow-hidden backdrop-blur-sm shadow-inner`}
        >
          <div
            className={`h-full ${strength.color} transition-all duration-300 shadow-lg`}
            style={{ width: `${(strength.strength / 5) * 100}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2">
          {hasLength ? (
            <div className="p-1 rounded-full bg-green-500/20">
              <Check size={14} className="text-green-400" />
            </div>
          ) : (
            <div className="p-1 rounded-full bg-red-500/20">
              <X size={14} className="text-red-400" />
            </div>
          )}
          <span className={`${theme.tailwind.texting2}`}>8+ characters</span>
        </div>
        <div className="flex items-center gap-2">
          {hasUpperCase ? (
            <div className="p-1 rounded-full bg-green-500/20">
              <Check size={14} className="text-green-400" />
            </div>
          ) : (
            <div className="p-1 rounded-full bg-red-500/20">
              <X size={14} className="text-red-400" />
            </div>
          )}
          <span className={`${theme.tailwind.texting2}`}>Uppercase letter</span>
        </div>
        <div className="flex items-center gap-2">
          {hasNumber ? (
            <div className="p-1 rounded-full bg-green-500/20">
              <Check size={14} className="text-green-400" />
            </div>
          ) : (
            <div className="p-1 rounded-full bg-red-500/20">
              <X size={14} className="text-red-400" />
            </div>
          )}
          <span className={`${theme.tailwind.texting2}`}>Number</span>
        </div>
        <div className="flex items-center gap-2">
          {hasSymbol ? (
            <div className="p-1 rounded-full bg-green-500/20">
              <Check size={14} className="text-green-400" />
            </div>
          ) : (
            <div className="p-1 rounded-full bg-red-500/20">
              <X size={14} className="text-red-400" />
            </div>
          )}
          <span className={`${theme.tailwind.texting2}`}>
            Special character
          </span>
        </div>
      </div>
    </div>
  );
};

const UserProfile = () => {
  const dispatch = useDispatch();
  const activeUser = useSelector((state) => state.auth.activeUser);
  const [isEditing, setIsEditing] = useState(false);
  const [userForm, setUserForm] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const { theme, gradient } = useSidebarContext();
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Fetch the active user and societies
    dispatch(checkActiveUser());
    dispatch(fetchSocieties());
  }, [dispatch]);

  useEffect(() => {
    if (activeUser) {
      // Initialize form with user data
      setUserForm({
        username: activeUser.username,
        role: activeUser.role,
        profile_picture:
          activeUser.profile_picture || "/api/placeholder/150/150",
        societies: activeUser.societies || [],
        adminData: activeUser.additionalData || {
          name: "",
          contactInfo: { phone: "", email: "", cnic: "", address: "" },
        },
        customerData: activeUser.additionalData || {
          name: "",
          contactInfo: {
            phone: "",
            email: "",
          },
        },
        employeeData: activeUser.additionalData || {
          name: "",
          designation: "",
          personalDetails: {
            phone: "",
            address: "",
          },
        },
        investorData: activeUser.additionalData || {
          name: "",
          investmentAmount: 0,
          contactInfo: {
            phone: "",
            email: "",
          },
        },
      });
    }
  }, [activeUser]);

  if (!userForm) {
    return (
      <div
        className={`flex justify-center items-center min-h-screen ${gradient}`}
      >
        <div className="animate-pulse flex flex-col items-center">
          <div
            className={`w-32 h-32 ${theme.tailwind.bg5} rounded-full mb-4`}
          ></div>
          <div className={`h-6 ${theme.tailwind.bg5} rounded w-48 mb-2`}></div>
          <div className={`h-4 ${theme.tailwind.bg5} rounded w-64`}></div>
        </div>
      </div>
    );
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfilePicture(file);
      setUserForm((prev) => ({
        ...prev,
        profile_picture: URL.createObjectURL(file),
      }));
    }
  };

  const handleEditProfile = async () => {
    if (isEditing && profilePicture) {
      try {
        if (!activeUser?.id) {
          console.error("User ID is missing");
          return;
        }

        // Prepare FormData
        const formData = new FormData();
        formData.append("profile_picture", profilePicture);
        // Append other form fields as necessary
        formData.append("username", userForm.username);
        formData.append("role", userForm.role);

        // Dispatch the action with FormData
        const result = await dispatch(
          updateUserInfo({ userId: activeUser.id, updatedData: formData })
        ).unwrap();

        // Update the user form state with the new profile picture URL
        setUserForm((prev) => ({
          ...prev,
          profile_picture: result.profile_picture || prev.profile_picture,
        }));
        setProfilePicture(null);
      } catch (error) {
        console.error("Error updating profile:", error);
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: error.message || "An unknown error occurred",
          background: `#1e293b`,
          color: `#94a3b8`,
          customClass: {
            popup: `rounded-2xl border ${theme.tailwind.border2}`,
            title: "text-red-400",
            confirmButton: `${theme.tailwind.bgr47} px-6 py-3 rounded-xl`,
          },
        });
      }
    }
    setIsEditing(!isEditing);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError("");

    const hasUpperCase = /[A-Z]/.test(passwordForm.password);
    const hasLowerCase = /[a-z]/.test(passwordForm.password);
    const hasNumber = /\d/.test(passwordForm.password);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.password);

    if (passwordForm.password !== passwordForm.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (passwordForm.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSymbol) {
      setError(
        "Password must contain uppercase, lowercase, number, and special character"
      );
      return;
    }

    try {
      await dispatch(
        resetPassword({
          userId: activeUser.id,
          newPassword: passwordForm.password,
        })
      ).unwrap();

      setShowResetModal(false);
      setPasswordForm({ password: "", confirmPassword: "" });

      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Password has been reset successfully",
        background: `#1e293b`,
        color: `#94a3b8`,
        customClass: {
          popup: `rounded-2xl border ${theme.tailwind.border2}`,
          title: `${theme.tailwind.text9}`,
          htmlContainer: `${theme.tailwind.texting2}`,
          confirmButton: `${theme.tailwind.bgr47} px-6 py-3 rounded-xl text-white ${theme.tailwind.groupHoverG11} transform hover:scale-105 transition-all duration-300`,
        },
      });
    } catch (error) {
      setError(error.message || "Failed to reset password");
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to reset password",
        background: `#1e293b`,
        color: `#94a3b8`,
        customClass: {
          popup: `rounded-2xl border ${theme.tailwind.border2}`,
          title: "text-red-400",
          htmlContainer: `${theme.tailwind.texting2}`,
          confirmButton: `${theme.tailwind.bgr47} px-6 py-3 rounded-xl text-white`,
        },
      });
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "Admin":
        return <Shield className="text-purple-400" size={20} />;
      case "Employee":
        return <Briefcase className="text-blue-400" size={20} />;
      case "Customer":
        return <User className="text-yellow-400" size={20} />;
      case "Investor":
        return <Clock className="text-green-400" size={20} />;
      default:
        return <User className={`${theme.tailwind.text}`} size={20} />;
    }
  };

  const { name, contactInfo, designation, personalDetails } =
    userForm.role === "Customer"
      ? userForm.customerData
      : userForm.role === "Employee"
      ? userForm.employeeData
      : userForm.role === "Investor"
      ? userForm.investorData
      : userForm.role === "Admin"
      ? userForm.adminData
      : {};

  return (
    <div className={`min-h-screen ${gradient} p-6 perspective-1000`}>
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Profile Header */}
        <div
          className={`relative ${theme.tailwind.bgr45} backdrop-blur-xl rounded-3xl shadow-2xl p-8 border ${theme.tailwind.border2} transform hover:scale-[1.01] transition-all duration-500 overflow-hidden group`}
        >
          {/* Background decorative elements */}
          <div
            className={`absolute -top-40 -right-40 w-96 h-96 ${theme.tailwind.bg25} rounded-full blur-3xl`}
          ></div>
          <div
            className={`absolute -bottom-24 -left-24 w-64 h-64 ${theme.tailwind.bg42} rounded-full blur-3xl`}
          ></div>

          {/* Glowing border effect */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden">
            <div
              className={`absolute inset-x-0 top-0 h-px ${theme.tailwind.bgr46}`}
            ></div>
            <div
              className={`absolute inset-x-0 bottom-0 h-px ${theme.tailwind.bgr8}`}
            ></div>
            <div
              className={`absolute inset-y-0 left-0 w-px ${theme.tailwind.BgBottom7}`}
            ></div>
            <div
              className={`absolute inset-y-0 right-0 w-px ${theme.tailwind.BgBottom1}`}
            ></div>
          </div>

          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-8 z-10">
            <div className="relative group">
              <div
                className={`absolute inset-0 rounded-full ${theme.tailwind.bgr47} blur-md opacity-50 group-hover:opacity-75 transition-opacity duration-500`}
              ></div>
              <div
                className={`absolute inset-1 rounded-full ${theme.tailwind.bg16}`}
              ></div>
              <img
                src={userForm.profile_picture}
                alt="Profile"
                className={`relative w-36 h-36 rounded-full border-4 ${theme.tailwind.border28} ${theme.tailwind.groupHover8} transition-all duration-500 z-10 transform group-hover:scale-105 shadow-xl object-cover`}
              />
              {isEditing && (
                <label
                  className={`absolute bottom-2 right-2 ${theme.tailwind.bgr47} p-3 rounded-full cursor-pointer shadow-lg transform hover:scale-110 transition-transform duration-300 z-20`}
                >
                  <Edit size={16} className="text-white" />
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
              )}
            </div>
            <div className="flex-1 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <h1
                  className={`text-4xl font-bold ${theme.tailwind.bgr22} text-transparent bg-clip-text`}
                >
                  {name || "No Name"}
                </h1>
                <div
                  className={`${theme.tailwind.bgr48} ${theme.tailwind.text9} px-4 py-1 rounded-full text-sm font-medium border ${theme.tailwind.border15} flex items-center gap-2 shadow-inner`}
                >
                  {getRoleIcon(userForm.role)}
                  {userForm.role}
                </div>
              </div>
              {userForm.role === "Employee" && (
                <p className={`${theme.tailwind.text11} text-lg mt-2`}>
                  {designation || "No Designation"}
                </p>
              )}

              {userForm.role === "Employee" && (
                <div className="flex flex-wrap items-center gap-4 mt-3">
                  <div
                    className={`flex items-center gap-2 ${theme.tailwind.texting2} ${theme.tailwind.bg2} px-4 py-2 rounded-xl backdrop-blur-sm`}
                  >
                    <Mail size={16} className={`${theme.tailwind.text}`} />
                    <span>{personalDetails?.cnic || "No CNIC"}</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 ${theme.tailwind.texting2} ${theme.tailwind.bg2} px-4 py-2 rounded-xl backdrop-blur-sm`}
                  >
                    <Phone size={16} className={`${theme.tailwind.text}`} />
                    <span>{personalDetails?.phone || "No Phone"}</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 ${theme.tailwind.texting2} ${theme.tailwind.bg2} px-4 py-2 rounded-xl backdrop-blur-sm`}
                  >
                    <Mail size={16} className={`${theme.tailwind.text}`} />
                    <span>{personalDetails?.address || "No Address"}</span>
                  </div>
                </div>
              )}
              {userForm.role !== "Employee" && (
                <div className="flex flex-wrap items-center gap-4 mt-3">
                  <div
                    className={`flex items-center gap-2 ${theme.tailwind.texting2} ${theme.tailwind.bg2} px-4 py-2 rounded-xl backdrop-blur-sm`}
                  >
                    <Mail size={16} className={`${theme.tailwind.text}`} />
                    <span>{contactInfo?.email || "No Email"}</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 ${theme.tailwind.texting2} ${theme.tailwind.bg2} px-4 py-2 rounded-xl backdrop-blur-sm`}
                  >
                    <Phone size={16} className={`${theme.tailwind.text}`} />
                    <span>{contactInfo?.phone || "No Phone"}</span>
                  </div>
                </div>
              )}
              <div className="flex gap-4 mt-6">
                <button
                  onClick={handleEditProfile}
                  className={`${theme.tailwind.bgr47} text-white px-6 py-3 rounded-xl flex items-center gap-2 transform hover:scale-105 transition-all duration-300 shadow-lg relative overflow-hidden group`}
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                  <Settings size={18} />
                  <span className="relative z-10">
                    {isEditing ? "Save Changes" : "Edit Profile"}
                  </span>
                </button>

                <button
                  onClick={() => setShowResetModal(true)}
                  className={`${theme.tailwind.bgr47} text-white px-6 py-3 rounded-xl flex items-center gap-2 transform hover:scale-105 transition-all duration-300 shadow-lg relative overflow-hidden group`}
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                  <Lock size={18} />
                  <span className="relative z-10">Reset Password</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Assigned Societies */}
        <div
          className={`${theme.tailwind.bgr45} backdrop-blur-xl rounded-3xl shadow-2xl p-8 border ${theme.tailwind.border2} transform hover:scale-[1.01] transition-all duration-500 relative overflow-hidden`}
        >
          {/* Background decorative elements */}
          <div
            className={`absolute -top-20 -left-20 w-64 h-64 ${theme.tailwind.bg63} rounded-full blur-3xl`}
          ></div>
          <div
            className={`absolute -bottom-40 -right-40 w-96 h-96 ${theme.tailwind.bg64} rounded-full blur-3xl`}
          ></div>

          {/* Glowing border effect */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden">
            <div
              className={`absolute inset-x-0 top-0 h-px ${theme.tailwind.bgr49}`}
            ></div>
            <div
              className={`absolute inset-x-0 bottom-0 h-px ${theme.tailwind.bgr1}`}
            ></div>
            <div
              className={`absolute inset-y-0 left-0 w-px ${theme.tailwind.BgBottom8}`}
            ></div>
            <div
              className={`absolute inset-y-0 right-0 w-px ${theme.tailwind.BgBottom9}`}
            ></div>
          </div>

          <div className="relative z-10">
            <h2
              className={`text-2xl font-bold ${theme.tailwind.text9} mb-6 flex items-center gap-2`}
            >
              <Building className={`${theme.tailwind.text}`} />
              Assigned Societies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {userForm.societies && userForm.societies.length > 0 ? (
                userForm.societies.map((society, idx) => (
                  <div
                    key={idx}
                    className={`${theme.tailwind.bg2} rounded-xl p-4 flex items-center gap-3 transform hover:translate-y-[-4px] hover:scale-[1.02] transition-all duration-300 border ${theme.tailwind.border2} ${theme.tailwind.hoverB1} group relative`}
                  >
                    <div
                      className={`absolute inset-0 rounded-xl ${theme.tailwind.bgr50} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                    ></div>
                    <div
                      className={`absolute inset-0 rounded-xl shadow-[inset_0_0_20px_rgba(45,212,191,0.05)] opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                    ></div>
                    <div
                      className={`p-2 rounded-lg ${theme.tailwind.bgGradient26} backdrop-blur-sm`}
                    >
                      <Home className={`${theme.tailwind.text}`} size={20} />
                    </div>
                    <span className={`${theme.tailwind.texting1} font-medium`}>
                      {society.name}
                    </span>
                  </div>
                ))
              ) : (
                <div
                  className={`${theme.tailwind.texting1} col-span-full flex items-center justify-center p-8 ${theme.tailwind.bg60} rounded-xl border ${theme.tailwind.border3}`}
                >
                  <span>No societies assigned</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <Transition show={showResetModal} as={React.Fragment}>
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
              <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div
                  className={`fixed inset-0 ${theme.tailwind.bg45} backdrop-blur-sm transition-opacity`}
                />
              </Transition.Child>

              <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-4 scale-95"
                className="relative z-50 w-full max-w-lg"
              >
                <div
                  className={`relative transform overflow-hidden rounded-2xl ${theme.tailwind.bgr2} backdrop-blur-xl px-4 pt-5 pb-4 text-left shadow-2xl transition-all sm:p-6 border ${theme.tailwind.border2}`}
                >
                  <div
                    className={`absolute top-0 left-0 right-0 h-1 ${theme.tailwind.bgr25}`}
                  ></div>
                  <div
                    className={`absolute -top-40 -right-40 w-64 h-64 ${theme.tailwind.bg63} rounded-full blur-3xl`}
                  ></div>
                  <div
                    className={`absolute -bottom-24 -left-24 w-40 h-40 ${theme.tailwind.bg64} rounded-full blur-3xl`}
                  ></div>

                  <div className="sm:flex sm:items-start">
                    <div
                      className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${theme.tailwind.bgr48} sm:mx-0 sm:h-10 sm:w-10`}
                    >
                      <Key className={`h-6 w-6 ${theme.tailwind.text9}`} />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3
                        className={`text-lg font-medium leading-6 ${theme.tailwind.text9}`}
                      >
                        Reset Password
                      </h3>
                      <div className="mt-2">
                        <p className={`text-sm ${theme.tailwind.texting2}`}>
                          Please enter your new password below. Make sure it's
                          at least 8 characters long.
                        </p>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handlePasswordReset} className="mt-5 sm:mt-6">
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={passwordForm.password}
                        onChange={(e) =>
                          setPasswordForm((prev) => ({
                            ...prev,
                            password: e.target.value,
                          }))
                        }
                        className={`w-full px-4 py-3 ${theme.tailwind.bg2} border ${theme.tailwind.border19} rounded-xl focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-300 ${theme.tailwind.texting1} ${theme.tailwind.placeHolder4} shadow-inner`}
                        placeholder="New Password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme.tailwind.texting2} ${theme.tailwind.hover1} transition-colors duration-300`}
                      >
                        {showPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>

                    <div className="relative mt-4">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordForm.confirmPassword}
                        onChange={(e) =>
                          setPasswordForm((prev) => ({
                            ...prev,
                            confirmPassword: e.target.value,
                          }))
                        }
                        className={`w-full px-4 py-3 ${theme.tailwind.bg2} border ${theme.tailwind.border19} rounded-xl focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-300 ${theme.tailwind.texting1} ${theme.tailwind.placeHolder4} shadow-inner`}
                        placeholder="Confirm Password"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme.tailwind.texting2} ${theme.tailwind.hover1} transition-colors duration-300`}
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>

                    {error && (
                      <div className="mt-2 p-2 bg-red-500/20 border border-red-500/30 rounded-lg">
                        <p className="text-red-400 text-sm">{error}</p>
                      </div>
                    )}

                    <PasswordStrengthIndicator
                      password={passwordForm.password}
                    />

                    <div className="mt-6 grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setShowResetModal(false)}
                        className={`w-full px-4 py-3 rounded-xl ${theme.tailwind.bg14} ${theme.tailwind.texting1} ${theme.tailwind.bgHover31} transition-all duration-300 border ${theme.tailwind.border2}`}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className={`w-full px-4 py-3 rounded-xl ${theme.tailwind.bgr47} text-white ${theme.tailwind.groupHoverG11} transform hover:scale-105 transition-all duration-300 relative overflow-hidden group`}
                      >
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                        <span className="relative z-10">Reset Password</span>
                      </button>
                    </div>
                  </form>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
};

export default UserProfile;
